import { useState, useEffect, useRef, useCallback, useId, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import '@/components/shared/accessibility/accessibility.css';

const STORAGE_KEY = 'perso_a11y_filters';
const CLASS_PREFIX = 'a11y-';

const SCREEN_FILTER_GROUP = ['visual-fatigue', 'migraine'];

/** Filtres actifs, par identifiant. */
type FiltersState = Record<string, boolean>;

const FILTERS = [
    {
        category: 'Vision',
        items: [
            { id: 'dyslexia', label: 'Dyslexie', tooltip: 'Police Arial/Verdana, espacement des lettres (+0.12em) et des mots (+0.16em), largeur max 70 caractères, alignement à gauche' },
            { id: 'senior', label: 'Senior', tooltip: 'Texte agrandi (18px), titres plus grands, interligne augmenté (1.5), cibles tactiles élargies (44px)' },
            { id: 'low-vision', label: 'Malvoyance', tooltip: 'Texte agrandi (20px), graisse moyenne (500), liens soulignés, interligne augmenté (1.5)' },
            { id: 'presbyopia', label: 'Presbytie', tooltip: 'Texte agrandi (20px), interligne augmenté (1.5), espacement des lettres (+0.12em) et des mots (+0.16em)' },
            { id: 'cataract', label: 'Cataracte', tooltip: 'Texte agrandi (18px), graisse moyenne (500), interligne augmenté (1.5), fond légèrement chaud pour réduire l\'éblouissement' },
            { id: 'macular-degen', label: 'DMLA', tooltip: 'Texte très agrandi (24px), espacement des mots doublé (+0.32em), interligne augmenté (1.8), graisse modérée (500)' },
        ],
    },
    {
        category: 'Confort',
        items: [
            { id: 'visual-fatigue', label: 'Fatigue visuelle', tooltip: 'Luminosité réduite (85%) et saturation réduite (80%)' },
            { id: 'migraine', label: 'Migraine', tooltip: 'Luminosité réduite (80%), saturation réduite (60%), toutes les animations et transitions désactivées' },
            { id: 'comfort', label: 'Confort de lecture', tooltip: 'Interligne augmenté (1.5), espacement des lettres (+0.12em) et des mots (+0.16em)' },
            { id: 'epilepsy', label: 'Épilepsie photosensible', tooltip: 'Toutes les animations et transitions désactivées, vidéos en pause automatique' },
            { id: 'attention', label: 'Trouble de l\'attention', tooltip: 'Animations désactivées, contour de focus renforcé (3px orange) pour mieux repérer l\'élément actif' },
        ],
    },
    {
        category: 'Motricité',
        items: [
            { id: 'motor-impairment', label: 'Motricité réduite', tooltip: 'Cibles tactiles élargies à 44px minimum sur les boutons, menus de navigation et champs de formulaire' },
        ],
    },
];

function loadState(): FiltersState {
    try {
        return JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}') as FiltersState;
    } catch {
        return {};
    }
}

function saveState(state: FiltersState) {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch { /* quota exceeded or private browsing */ }
}

function applyClasses(state: FiltersState) {
    const html = document.documentElement;
    const allIds = FILTERS.flatMap(cat => cat.items.map(item => item.id));

    allIds.forEach(id => {
        html.classList.toggle(CLASS_PREFIX + id, !!state[id]);
    });
}

function handleEpilepsy(active: boolean) {
    const videos = document.querySelectorAll<HTMLVideoElement>('video[autoplay]');
    videos.forEach(video => {
        if (active) {
            video.pause();
        } else {
            void video.play();
        }
    });
}

function Tooltip({ text, filterLabel }: { text: string; filterLabel?: string }) {
    const [visible, setVisible] = useState(false);
    const [position, setPosition] = useState({ top: 0, left: 0 });
    const iconRef = useRef<HTMLSpanElement>(null);
    const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const tooltipId = useId();

    const cancelHide = useCallback(() => {
        if (hideTimerRef.current) {
            clearTimeout(hideTimerRef.current);
            hideTimerRef.current = null;
        }
    }, []);

    const show = useCallback(() => {
        if (!iconRef.current) return;
        cancelHide();
        setVisible(true);

        const rect = iconRef.current.getBoundingClientRect();
        const topPos = rect.top - 8;
        setPosition({
            top: topPos < 60 ? rect.bottom + 8 : topPos,
            left: 0,
        });
    }, [cancelHide]);

    const hide = useCallback(() => {
        cancelHide();
        hideTimerRef.current = setTimeout(() => setVisible(false), 400);
    }, [cancelHide]);

    useEffect(() => () => cancelHide(), [cancelHide]);

    useEffect(() => {
        if (!visible) return;
        const handleKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                cancelHide();
                setVisible(false);
            }
        };
        document.addEventListener('keydown', handleKey);
        return () => document.removeEventListener('keydown', handleKey);
    }, [visible, cancelHide]);

    return (
        <span
            className="a11y-info"
            ref={iconRef}
            tabIndex={0}
            role="button"
            aria-label={filterLabel ? `Détails sur ${filterLabel}` : 'Détails du filtre'}
            aria-describedby={tooltipId}
            onMouseEnter={show}
            onMouseLeave={hide}
            onFocus={show}
            onBlur={hide}
        >
            <span className="a11y-info__icon" aria-hidden="true">?</span>
            <span
                id={tooltipId}
                className={`a11y-tooltip${visible ? ' is-visible' : ''}`}
                role="tooltip"
                style={visible ? { top: position.top, left: position.left } : undefined}
                onMouseEnter={cancelHide}
                onMouseLeave={hide}
            >
                {text}
            </span>
        </span>
    );
}

interface FilterRowProps {
    id: string;
    label: string;
    tooltip: string;
    checked: boolean;
    onChange: (id: string, checked: boolean) => void;
}

function FilterRow({ id, label, tooltip, checked, onChange }: FilterRowProps) {
    const labelId = `a11y-filter-label-${id}`;
    return (
        <div className="a11y-filter">
            <span className="a11y-filter__left">
                <span id={labelId} className="a11y-filter__label">{label}</span>
                <Tooltip text={tooltip} filterLabel={label} />
            </span>
            <label className="a11y-switch">
                <input
                    type="checkbox"
                    role="switch"
                    checked={checked}
                    aria-checked={checked}
                    aria-labelledby={labelId}
                    onChange={e => onChange(id, e.target.checked)}
                />
                <span className="a11y-toggle" aria-hidden="true" />
            </label>
        </div>
    );
}

interface AccessibilityPanelProps {
    closeIcon?: ReactNode;
    isOpen?: boolean;
    onToggle?: (open: boolean) => void;
}

export default function AccessibilityPanel({ closeIcon, isOpen: controlledIsOpen, onToggle }: AccessibilityPanelProps = {}) {
    const [internalIsOpen, setInternalIsOpen] = useState(false);
    const isControlled = controlledIsOpen !== undefined;
    const isOpen = isControlled ? controlledIsOpen : internalIsOpen;
    const [state, setState] = useState(loadState);
    const panelRef = useRef<HTMLDivElement>(null);
    const triggerRef = useRef<HTMLButtonElement>(null);
    const lastFocusedRef = useRef<HTMLElement | null>(null);

    // Apply classes on state change
    useEffect(() => {
        applyClasses(state);
        saveState(state);
    }, [state]);

    // Handle epilepsy on mount
    useEffect(() => {
        if (state.epilepsy) handleEpilepsy(true);
    }, []);

    // Focus trap & Escape
    useEffect(() => {
        if (!isOpen) return;

        const focusableSelector = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

        function handleKeyDown(e: KeyboardEvent) {
            if (e.key === 'Escape') {
                closePanel();
                return;
            }
            if (e.key === 'Tab' && panelRef.current) {
                const focusable = panelRef.current.querySelectorAll<HTMLElement>(focusableSelector);
                if (focusable.length === 0) return;
                const first = focusable[0];
                const last = focusable[focusable.length - 1];

                if (e.shiftKey && document.activeElement === first) {
                    e.preventDefault();
                    last.focus();
                } else if (!e.shiftKey && document.activeElement === last) {
                    e.preventDefault();
                    first.focus();
                }
            }
        }

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [isOpen]);

    function openPanel() {
        lastFocusedRef.current = document.activeElement as HTMLElement | null;
        if (isControlled) {
            onToggle?.(true);
        } else {
            setInternalIsOpen(true);
        }
        document.body.style.overflow = 'hidden';

        setTimeout(() => {
            if (panelRef.current) {
                const firstFocusable = panelRef.current.querySelector<HTMLElement>('button, [href], input, [tabindex]:not([tabindex="-1"])');
                if (firstFocusable) firstFocusable.focus();
            }
        }, 50);
    }

    function closePanel() {
        if (isControlled) {
            onToggle?.(false);
        } else {
            setInternalIsOpen(false);
        }
        document.body.style.overflow = '';

        if (lastFocusedRef.current) {
            lastFocusedRef.current.focus();
        }
    }

    function toggleFilter(id: string, checked: boolean) {
        setState(prev => {
            const next = { ...prev };

            // Exclusive groups
            if (checked && SCREEN_FILTER_GROUP.includes(id)) {
                SCREEN_FILTER_GROUP.forEach(gid => {
                    if (gid !== id) next[gid] = false;
                });
            }

            next[id] = checked;

            if (id === 'epilepsy') handleEpilepsy(checked);

            return next;
        });
    }

    function resetAll() {
        setState({});
        handleEpilepsy(false);
    }

    return (
        <>
            <button
                className="a11y-trigger"
                type="button"
                ref={triggerRef}
                aria-label="Ouvrir le panneau d'accessibilité"
                aria-expanded={isOpen}
                aria-controls="a11y-panel"
                onClick={() => (isOpen ? closePanel() : openPanel())}
            >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" fill="currentColor" width="28" height="28" aria-hidden="true" focusable="false">
                    <path d="M256 48a208 208 0 1 1 0 416 208 208 0 0 1 0-416zm0-48C114.6 0 0 114.6 0 256s114.6 256 256 256 256-114.6 256-256S397.4 0 256 0z" />
                    <circle cx="256" cy="120" r="36" />
                    <path d="M352 168H160c-13.3 0-24 10.7-24 24s10.7 24 24 24h56v40l-43.2 115.2c-4.6 12.2 1.6 25.8 13.8 30.4 12.2 4.6 25.8-1.6 30.4-13.8L256 272l39 115.8c4.6 12.2 18.2 18.4 30.4 13.8 12.2-4.6 18.4-18.2 13.8-30.4L296 256v-40h56c13.3 0 24-10.7 24-24s-10.7-24-24-24z" />
                </svg>
            </button>

            {createPortal(
                <>
                    <div
                        className={`a11y-backdrop${isOpen ? ' is-visible' : ''}`}
                        aria-hidden="true"
                        onClick={closePanel}
                    />

                    <div
                        id="a11y-panel"
                        className={`a11y-panel${isOpen ? ' is-open' : ''}`}
                        role="dialog"
                        aria-modal="true"
                        aria-label="Paramètres d'accessibilité"
                        ref={panelRef}
                        hidden={!isOpen}
                    >
                        <div className="a11y-panel__header">
                            <h2 className="a11y-panel__title">Accessibilité</h2>
                            <div className="a11y-panel__actions">
                                <button type="button" className="a11y-panel__reset" onClick={resetAll}>
                                    Réinitialiser
                                </button>
                                <button type="button" className="a11y-panel__close" aria-label="Fermer le panneau d'accessibilité" onClick={closePanel}>
                                    {closeIcon || <span aria-hidden="true" style={{ fontSize: 20, lineHeight: 1 }}>✕</span>}
                                </button>
                            </div>
                        </div>

                        <div className="a11y-panel__body">
                            {FILTERS.map(cat => (
                                <fieldset className="a11y-category" key={cat.category}>
                                    <legend>{cat.category}</legend>
                                    {cat.items.map(item => (
                                        <FilterRow
                                            key={item.id}
                                            id={item.id}
                                            label={item.label}
                                            tooltip={item.tooltip}
                                            checked={!!state[item.id]}
                                            onChange={toggleFilter}
                                        />
                                    ))}
                                </fieldset>
                            ))}
                        </div>
                    </div>
                </>,
                document.body
            )}
        </>
    );
}
